import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { adminAPI } from '@/lib/api';
import { Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { RoleBadge } from '@/components/admin/shared';
import { AdminStats } from '@/components/admin/AdminStats';
import { AdminInvitesPanel } from '@/components/admin/AdminInvitesPanel';
import { AdminUsersTable } from '@/components/admin/AdminUsersTable';
import { AdminAuditLog } from '@/components/admin/AdminAuditLog';
import { AdminOrphanedPanel } from '@/components/admin/AdminOrphanedPanel';
import {
  AdminEditDialog, AdminDeleteDialog, AdminInviteDialog,
} from '@/components/admin/AdminUserDialogs';
import { AdminBulkDialog } from '@/components/admin/AdminBulkDialog';

export default function AdminPage() {
  const { user, refreshUser } = useAuth();
  const isSuper = user?.role === 'super_admin';

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [auditData, setAuditData] = useState({ items: [], total: 0, has_more: false });
  const [auditParams, setAuditParams] = useState({ limit: 25, offset: 0 });
  const [orphanData, setOrphanData] = useState({ items: [], total: 0, by_kind: { avatar: 0, video: 0 }, has_more: false });
  const [orphanParams, setOrphanParams] = useState({ limit: 20, offset: 0 });
  const [purgingOrphans, setPurgingOrphans] = useState(false);

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [inviting, setInviting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('admin');
  const [createdInvite, setCreatedInvite] = useState(null);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('plan');
  const [bulkPlan, setBulkPlan] = useState('starter');
  const [bulkCreditsDelta, setBulkCreditsDelta] = useState(10);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => { load(); }, []);

  // Refetch audit log when filters/pagination change
  useEffect(() => {
    adminAPI.auditLog(auditParams).then(r => setAuditData(r.data)).catch(() => {});
  }, [auditParams]);

  // Refetch orphans on filter/pagination change (super admin only)
  useEffect(() => {
    if (!isSuper) return;
    adminAPI.listOrphans(orphanParams).then(r => setOrphanData(r.data)).catch(() => {});
  }, [orphanParams, isSuper]);

  const load = async () => {
    setLoading(true);
    try {
      const calls = [
        adminAPI.stats(),
        adminAPI.listUsers(),
        adminAPI.listInvites(),
        adminAPI.auditLog(auditParams),
      ];
      if (isSuper) calls.push(adminAPI.listOrphans(orphanParams));
      const results = await Promise.all(calls);
      setStats(results[0].data);
      setUsers(results[1].data);
      setInvites(results[2].data);
      setAuditData(results[3].data);
      if (isSuper && results[4]) setOrphanData(results[4].data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const refreshAfterAction = async () => {
    try {
      const [u, audit, s] = await Promise.all([
        adminAPI.listUsers(),
        adminAPI.auditLog(auditParams),
        adminAPI.stats(),
      ]);
      setUsers(u.data);
      setAuditData(audit.data);
      setStats(s.data);
      if (isSuper) {
        const orph = await adminAPI.listOrphans(orphanParams);
        setOrphanData(orph.data);
      }
    } catch { /* ignore */ }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u => (u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q));
  }, [users, search]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = (visible) => {
    const visibleIds = visible.map(u => u.id);
    const allSelected = visibleIds.every(id => selectedIds.includes(id));
    setSelectedIds(allSelected
      ? selectedIds.filter(id => !visibleIds.includes(id))
      : [...new Set([...selectedIds, ...visibleIds])]);
  };

  const openEdit = (u) => {
    if (!u) { setEditing(null); return; }
    setEditing({
      ...u,
      _plan: u.plan || 'free',
      _role: u.role || 'user',
      _monthly: u.monthly_credits ?? 0,
      _purchased: u.purchased_credits ?? 0,
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        plan: editing._plan,
        monthly_credits: Number(editing._monthly),
        purchased_credits: Number(editing._purchased),
      };
      if (isSuper && editing._role !== (editing.role || 'user')) {
        payload.role = editing._role;
      }
      await adminAPI.updateUser(editing.id, payload);
      toast.success('User updated');
      setEditing(null);
      await refreshAfterAction();
      if (editing.id === user?.id) refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await adminAPI.deleteUser(deleting.id);
      toast.success('User deleted');
      setDeleting(null);
      await refreshAfterAction();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const submitInvite = async (e) => {
    e?.preventDefault?.();
    if (!inviteEmail.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setInviting(true);
    try {
      const res = await adminAPI.createInvite({ email: inviteEmail.trim().toLowerCase(), role: inviteRole });
      setCreatedInvite(res.data);
      setInviteEmail('');
      setInviteRole('admin');
      const inv = await adminAPI.listInvites();
      setInvites(inv.data);
      const log = await adminAPI.auditLog(auditParams);
      setAuditData(log.data);
      toast.success('Invite created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invite');
    } finally {
      setInviting(false);
    }
  };

  const revokeInvite = async (id) => {
    try {
      await adminAPI.revokeInvite(id);
      const inv = await adminAPI.listInvites();
      setInvites(inv.data);
      const log = await adminAPI.auditLog(auditParams);
      setAuditData(log.data);
      toast.success('Invite revoked');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke invite');
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Invite link copied');
  };

  const runOrphanPurge = async () => {
    setPurgingOrphans(true);
    try {
      const res = await adminAPI.purgeOrphans();
      const { purged, pending_delete_api: pending, failed } = res.data;
      if (purged > 0) toast.success(`Purged ${purged} object${purged === 1 ? '' : 's'}`);
      else if (pending > 0) toast.info(`${pending} pending — storage DELETE not supported yet. Will retry automatically.`);
      else toast.success('Nothing to purge');
      const orph = await adminAPI.listOrphans(orphanParams);
      setOrphanData(orph.data);
      const audit = await adminAPI.auditLog(auditParams);
      setAuditData(audit.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    } finally {
      setPurgingOrphans(false);
    }
  };

  const runBulk = async () => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      if (bulkAction === 'plan') {
        await adminAPI.bulkUpdate({ user_ids: selectedIds, plan: bulkPlan });
        toast.success(`Updated plan for ${selectedIds.length} users`);
      } else if (bulkAction === 'credits') {
        await adminAPI.bulkUpdate({ user_ids: selectedIds, purchased_credits_delta: Number(bulkCreditsDelta) });
        toast.success(`Adjusted credits for ${selectedIds.length} users`);
      } else if (bulkAction === 'delete') {
        const res = await adminAPI.bulkDelete({ user_ids: selectedIds });
        toast.success(res.data.message);
      }
      setSelectedIds([]);
      setBulkOpen(false);
      await refreshAfterAction();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-8 max-w-7xl mx-auto" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <Shield className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Operator Console</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage users, roles, plans and credits across ClipParrot.
            </p>
          </div>
        </div>
        <RoleBadge role={user?.role} />
      </div>

      <AdminStats stats={stats} />

      <AdminInvitesPanel
        invites={invites}
        onOpenCreate={() => { setCreatedInvite(null); setInviteOpen(true); }}
        onCopyLink={copyLink}
        onRevoke={revokeInvite}
      />

      <AdminUsersTable
        users={filtered}
        currentUserId={user?.id}
        isSuper={isSuper}
        search={search}
        onSearch={setSearch}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        onOpenEdit={openEdit}
        onOpenDelete={setDeleting}
        onOpenBulk={() => setBulkOpen(true)}
      />

      <AdminAuditLog
        data={auditData}
        params={auditParams}
        onChangeParams={setAuditParams}
      />

      {isSuper && (
        <AdminOrphanedPanel
          data={orphanData}
          params={orphanParams}
          onChangeParams={setOrphanParams}
          onPurge={runOrphanPurge}
          purging={purgingOrphans}
        />
      )}

      <AdminEditDialog
        editing={editing}
        setEditing={setEditing}
        onClose={() => setEditing(null)}
        onSave={saveEdit}
        saving={saving}
        isSuper={isSuper}
      />

      <AdminDeleteDialog
        deleting={deleting}
        onCancel={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />

      <AdminInviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        isSuper={isSuper}
        inviteEmail={inviteEmail}
        setInviteEmail={setInviteEmail}
        inviteRole={inviteRole}
        setInviteRole={setInviteRole}
        createdInvite={createdInvite}
        setCreatedInvite={setCreatedInvite}
        inviting={inviting}
        onSubmit={submitInvite}
        onCopyLink={copyLink}
      />

      <AdminBulkDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        selectedCount={selectedIds.length}
        isSuper={isSuper}
        bulkAction={bulkAction}
        setBulkAction={setBulkAction}
        bulkPlan={bulkPlan}
        setBulkPlan={setBulkPlan}
        bulkCreditsDelta={bulkCreditsDelta}
        setBulkCreditsDelta={setBulkCreditsDelta}
        busy={bulkBusy}
        onRun={runBulk}
      />
    </div>
  );
}
